<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";

export interface IListTag {
  email?: string | null;
  displayValue?: string | null;
}
defineProps({
  list: {
    type: Array as PropType<IListTag[]>,
  },
  title: {
    type: String,
  },
  error: {
    type: Object as PropType<{ error: boolean; message: string | null } | null>,
    default: { error: false, message: null },
  },
  /**
   * Opt-in: each chip gets a close cross and emits `remove`. Off by default — the participants and
   * managers lists are derived from the attendee selection, so a cross there would suggest a write
   * the list cannot make. The parent owns the removal.
   */
  removable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: "remove", tag: IListTag, index: number): void;
}>();
</script>

<template>
  <div>
    <slot name="header">
      <h5 v-if="title" class="text-secondary-color mb-2">{{ title }}</h5>
    </slot>
    <div class="flex gap-1 flex-wrap">
      <!-- The container-tag classes carry no style: E2E selectors rely on them. -->
      <!-- Tighter right padding when removable: the close icon's box already leaves ~5px blank after the cross. -->
      <div
        v-for="(tag, index) in list"
        v-if="list?.length"
        :key="`tag-${index}`"
        class="container-tag flex items-center gap-2 w-fit h-8 py-1 pl-3 rounded-xl border border-stroke-color bg-light-color cursor-pointer"
        :class="removable ? 'container-tag--removable pr-2' : 'pr-4'"
      >
        <Tooltip
          v-if="tag.email && tag.displayValue"
          :position="'top-mid'"
          class="ml-1"
          :force-position-top="5"
          append-to-body
          minified
        >
          <template #img>
            <span class="text-tertiary-color">{{ tag.email }}</span>
          </template>
          <template #content>
            <div class="flex w-fit whitespace-nowrap">
              <span>{{ tag.displayValue }}</span>
            </div>
          </template>
        </Tooltip>
        <span v-else class="text-tertiary-color">{{ tag.email }}</span>
        <!-- Sibling of the Tooltip, not inside its #img: the cross must not be a tooltip trigger. -->
        <SvgIcon
          v-if="removable"
          name="close"
          class="w-4 h-4 cursor-pointer text-secondary-color shrink-0"
          @click.stop="emit('remove', tag, index)"
        ></SvgIcon>
      </div>
      <span v-else-if="error?.error" class="body_regular text-primary-red-color">{{
        error?.message || "Un destinataire principal est obligatoire"
      }}</span>
    </div>
  </div>
</template>
